import { useServices, useTheme } from '@/app/providers';
import { NotificationBell } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { UserMenuToggleV2 } from '@/modules/identity/ui/components/UserMenuToggleV2';
import { Logo } from '@/shared/components';
import { AccessRestrictedBanner } from '@/shared/components/AccessRestrictedBanner';
import { useI18n } from '@/shared/i18n';
import { NAV_CATEGORIES, PATH_TO_ID } from '@/shared/navigation';
import { ChevronDown, LogOut, Menu, Moon, Sun, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

interface MainLayoutProps {
  children: React.ReactNode;
}


export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>('main');
  const { t, language, setLanguage } = useI18n();
  const { isDark, toggleTheme } = useTheme();
  const ability = useAbility();
  const { currentUser, logout, systemSettings } = useServices();
  const location = useLocation();
  const navigate = useNavigate();

  // Get current page ID from path (handle nested routes like /admin/*)
  const getCurrentPage = () => {
    const path = location.pathname;
    // Check for admin sub-routes
    if (path.startsWith('/admin')) {
      return 'admin';
    }
    return PATH_TO_ID[path] || 'dashboard';
  };
  const currentPage = getCurrentPage();

  const filteredCategories = useMemo(() => {
    if (!currentUser) return [];
    return NAV_CATEGORIES.map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        const hasPermission = ability.can(item.permission.action, item.permission.subject as any);
        // OJO: antes esto era `hasRequiredRole || hasPermission`, lo cual anulaba por completo
        // el filtro de tenantRole (si ya tenías el permiso, el OR lo volvía true sin importar
        // el rol) — por eso ítems marcados `tenantRole: 'GUARD'` (Proveedores, Registro de
        // accesos, Incidentes, etc.) se colaban para RESIDENT/FAMILIAR, que suelen tener el
        // permiso de lectura de forma acotada a lo propio. Debe ser AND: ambos requisitos.
        const hasRequiredRole = !item.tenantRole
          || (Array.isArray(item.tenantRole)
            ? item.tenantRole.includes(currentUser.tenantRole)
            : currentUser.tenantRole === item.tenantRole);
        const moduleEnabled = !item.moduleKey || systemSettings?.features?.[item.moduleKey] !== false;
        return hasPermission && hasRequiredRole && moduleEnabled;
      }),
    })).filter((category) => category.items.length > 0);
  }, [ability, currentUser]);

  useEffect(() => {
    const activeCategory = filteredCategories.find(cat =>
      cat.items.some(item => item.id === currentPage)
    );
    if (activeCategory) {
      setExpandedCategory(activeCategory.id);
    }
  }, [currentPage, filteredCategories]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(prev => (prev === categoryId ? null : categoryId));
  };

  if (!currentUser) return null;

  // Use purple as default, but allow branding override
  const primaryColor = systemSettings?.branding?.primaryColor || '#8B5CF6';
  const logoUrl = systemSettings?.branding?.logoUrl;
  const appName = systemSettings?.branding?.appName || 'Hommi';

  return (
    <div className="min-h-screen bg-theme flex flex-col md:flex-row font-sans">
      {/* Mobile Header */}
      <div className="md:hidden glass-heavy p-4 flex justify-between items-center shadow-sm z-20 sticky top-0 border-b border-theme-light">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-8 object-contain" />
          ) : (
            <Logo size="md" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-surface-secondary transition-colors"
            aria-label={isDark ? t('settings.lightMode') : t('settings.darkMode')}
          >
            {isDark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-theme-secondary" />}
          </button>
          <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 text-theme-secondary">
            {isSidebarOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-30 w-64 sidebar-glass transform transition-transform duration-300 ease-in-out
        md:relative md:translate-x-0 flex flex-col
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo Section */}
        <div className="px-4 py-8 border-b border-theme-light flex flex-col items-center justify-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-20 h-20 object-contain" />
          ) : (
            <Logo size="xl" />
          )}
          <p className="text-xs text-theme-tertiary text-center">Panel Residencial</p>
        </div>

        {/* User Profile Section */}
        <div className="p-4 pb-0" onClick={() => setSidebarOpen(false)}>
          <UserMenuToggleV2 />
        </div>

        {/* Navigation - scrollable area */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <nav className="space-y-2">
            {filteredCategories.map((category) => {
              const visibleItems = category.items;

              const isExpanded = expandedCategory === category.id;
              const hasActiveItem = visibleItems.some(item => item.id === currentPage);

              return (
                <div key={category.id}>
                  {/* Category header */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors
                      ${hasActiveItem ? 'text-primary-600 dark:text-primary-400' : 'text-theme-tertiary'}
                      hover:bg-surface-secondary
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <category.icon size={14} />
                      {t(category.labelKey)}
                    </div>
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Collapsible items */}
                  {isExpanded && (
                    <div className="mt-1 space-y-1 ml-2">
                      {visibleItems.map(item => {
                        // For admin route, check if current path starts with /admin
                        const isAdminActive = item.path === '/admin' && location.pathname.startsWith('/admin');
                        return (
                          <NavLink
                            key={item.id}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) => {
                              const active = isActive || isAdminActive;
                              return `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                              ${active
                                ? 'gradient-primary text-white shadow-lg shadow-primary-500/30'
                                : 'text-theme-secondary hover:bg-surface-secondary hover:text-theme'
                              }`;
                            }}
                          >
                            <item.icon size={16} />
                            {t(item.labelKey)}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="p-4 border-t border-theme-light mt-auto">
          {/* Language Toggle */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setLanguage('es')}
              className={`flex-1 py-1.5 text-xs rounded-lg border transition-all ${
                language === 'es'
                  ? 'gradient-primary text-white border-transparent'
                  : 'border-theme text-theme-secondary hover:border-primary-400 hover:text-theme'
              }`}
            >
              ES
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`flex-1 py-1.5 text-xs rounded-lg border transition-all ${
                language === 'en'
                  ? 'gradient-primary text-white border-transparent'
                  : 'border-theme text-theme-secondary hover:border-primary-400 hover:text-theme'
              }`}
            >
              EN
            </button>
          </div>

          {/* Logout */}
          <button
            onClick={logout}
            className="flex items-center gap-3 text-theme-secondary hover:text-red-500 w-full px-3 py-2 text-sm transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <LogOut size={18} />
            {t('menu.logout')}
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-[calc(100vh-64px)] md:h-screen">
        {/* Desktop Header */}
        <header className="hidden md:flex items-center justify-end gap-3 px-8 py-4 border-b border-theme-light bg-theme">
          <NotificationBell />
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-surface-secondary transition-colors"
            aria-label={isDark ? t('settings.lightMode') : t('settings.darkMode')}
          >
            {isDark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-theme-secondary" />}
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-theme">
          <div className="max-w-7xl mx-auto animate-fade-in">
            <AccessRestrictedBanner />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
